/**
 * L1-FE-03 — Upload templates page.
 *
 * One row per upload type with metadata + a Download button that calls
 * `GET /uploads/templates/{type}/blank?period=yyyy-mm`. Per L1-BE-05 the
 * BE returns a direct CSV with `Content-Disposition: attachment` (no
 * signed-URL preflight).
 *
 * Acceptance note: the ticket says "without inlining the file in JS heap
 * (verified via `window.location.assign` or `<a download>` pattern)".
 * That conflicts with authenticated GETs — the browser strips
 * `Authorization` headers from anchor-click navigations and
 * `window.location.assign`. We keep `apiFetchBlob` + `URL.createObjectURL`
 * here, which DOES inline into JS heap. Templates are tiny (~10 lines of
 * CSV per the L1-BE-05 acceptance), so the trade-off is benign. If the
 * endpoint ever serves megabyte-class files, we'll need cookie-auth or a
 * signed pre-URL — not a problem the contract presents today.
 */

import {
  Badge,
  Button,
  Card,
  Code,
  Group,
  List,
  Loader,
  Paper,
  Select,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCloudUpload, IconDownload, IconFileText } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useBatches, useDownloadTemplate } from "../api/hooks";
import type { UploadType } from "../api/types";
import { useUploadTypeMeta } from "../auth/useBranding";
import { pickActiveBatch } from "../lib/batchState";
import { downloadBlob } from "../lib/download";
import { formatApiError } from "../lib/errors";
import { DOCUMENT_UPLOAD_TYPES, UPLOAD_TYPE_ORDER, type UploadTypeMeta } from "../lib/uploadTypes";
import { UploadsTabs } from "./uploads/UploadsTabs";

// Template metadata comes from the shared registry (lib/uploadTypes.ts), so
// the column lists shown here are the EXACT headers the backend's schemas
// expect — the same tuples the auto-detect route matches an uploaded file
// against. (The hand-maintained copy this replaced had drifted from the
// backend on four of six types.)
type TemplateMeta = {
  type: UploadType;
  label: string;
  description: string;
  columns: readonly string[];
  accept: string;
  maxSizeMb: number;
};

const templatesFor = (registry: Record<UploadType, UploadTypeMeta>): TemplateMeta[] =>
  UPLOAD_TYPE_ORDER.map((type) => {
    const meta = registry[type];
    return {
      type,
      label: meta.label,
      description: meta.description,
      columns: meta.columns,
      accept: Object.values(meta.accept).flat().join(", "),
      maxSizeMb: meta.maxSizeMb,
    };
  });

export function UploadTemplatesPage() {
  const registry = useUploadTypeMeta();
  const templates = useMemo(() => templatesFor(registry), [registry]);
  const batchesQ = useBatches();
  const batches = batchesQ.data?.items ?? [];

  // Default to the active batch's period — same defaulting policy as /uploads
  // (via the shared pickActiveBatch) so the templates carry the period the user
  // is actively working on, not an older Open period baked into the CSV. The
  // selector defaults here and only diverges once the user picks another, so
  // the value is derived rather than mirrored in via an effect.
  const defaultPeriod = useMemo(
    () => pickActiveBatch(batches)?.period_start.slice(0, 7) ?? null,
    [batches],
  );
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(null);
  const period = selectedPeriod ?? defaultPeriod;

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Group justify="space-between" wrap="wrap" gap="md">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="indigo" size={36} radius="md">
              <IconCloudUpload size={20} />
            </ThemeIcon>
            <Title order={2}>Upload templates</Title>
          </Group>
          <UploadsTabs />
        </Group>
        <Text c="dimmed" size="sm" maw={720}>
          Pre-populated blank templates for each upload type. The backend bakes the period and known
          site list into the file so you can paste straight into the customer's source-of-truth
          spreadsheet.
        </Text>
      </Stack>

      <Paper p="md" radius="md" withBorder>
        <Group gap="md" wrap="nowrap">
          <Text size="sm" fw={500}>
            Period
          </Text>
          <Select
            w={260}
            data={Array.from(new Set(batches.map((b) => b.period_start.slice(0, 7)))).map((p) => ({
              value: p,
              label: p,
            }))}
            value={period}
            onChange={setSelectedPeriod}
            placeholder={batchesQ.isPending ? "Loading…" : "Select a period"}
            disabled={batchesQ.isPending}
          />
        </Group>
      </Paper>

      <Stack gap="md">
        {templates.map((meta) => (
          <TemplateRow key={meta.type} meta={meta} period={period} />
        ))}
      </Stack>
    </Stack>
  );
}

function TemplateRow({ meta, period }: { meta: TemplateMeta; period: string | null }) {
  const templateMutation = useDownloadTemplate();
  const isDocument = DOCUMENT_UPLOAD_TYPES.has(meta.type);

  const onDownload = async () => {
    if (!period) return;
    try {
      const result = await templateMutation.mutateAsync({ type: meta.type, period });
      downloadBlob(result.blob, result.filename ?? `${meta.type}_${period}.csv`);
    } catch (err) {
      notifications.show({
        title: "Template download failed",
        message: formatApiError(err),
        color: "red",
        autoClose: 5000,
      });
    }
  };

  return (
    <Card withBorder radius="md" padding="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Group gap="md" wrap="nowrap" align="flex-start" style={{ flex: 1, minWidth: 0 }}>
          <ThemeIcon variant="light" color="indigo" radius="md" size={36}>
            <IconFileText size={18} />
          </ThemeIcon>
          <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="sm" wrap="wrap">
              <Text fw={600} size="sm">
                {meta.label}
              </Text>
              <Badge variant="default" size="xs">
                {meta.accept}
              </Badge>
              <Text size="xs" c="dimmed">
                max {meta.maxSizeMb} MB
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {meta.description}
            </Text>
            {isDocument ? (
              // A document has no header to advertise and no blank to fill: the
              // dates come from the certificate itself, through the form beside
              // the dropzone on the Portal tab.
              <Text size="xs" c="dimmed">
                Filed with its dates from the Portal tab — no template to fill.
              </Text>
            ) : (
              <Stack gap={2}>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} lts="0.06em">
                  Expected columns
                </Text>
                <List size="xs" listStyleType="none" withPadding={false}>
                  <List.Item>
                    <Group gap={6} wrap="wrap">
                      {meta.columns.map((col) => (
                        <Code key={col}>{col}</Code>
                      ))}
                    </Group>
                  </List.Item>
                </List>
              </Stack>
            )}
          </Stack>
        </Group>
        {isDocument ? null : (
          <Button
            size="sm"
            variant="light"
            leftSection={
              templateMutation.isPending ? <Loader size={14} /> : <IconDownload size={14} />
            }
            onClick={onDownload}
            loading={templateMutation.isPending}
            disabled={!period}
          >
            Download
          </Button>
        )}
      </Group>
      {!period ? (
        <Text size="xs" c="dimmed" mt="xs">
          Select a period above to enable download.
        </Text>
      ) : null}
    </Card>
  );
}
